<template>
  <div class="chat-conversation" ref="chatConversation">
    <div
      v-for="(message, index) in messages"
      :key="index"
      :class="['message', message.role]"
    >
      <div v-if="message.role === 'assistant'" class="ai-icon" :style="aiIconStyle"></div>

      <div class="chat-wrapper">
        <!-- Progress indicator for long-running tasks (shown before first token arrives) -->
        <div v-if="message.streaming && !message.content" class="progress-indicator">
          <div class="progress-dots">
            <span class="progress-dot"></span>
            <span class="progress-dot"></span>
            <span class="progress-dot"></span>
          </div>
          <span class="progress-label">Generating response...</span>
        </div>
        <!-- Attachment preview for user messages (live or from history via html_content) -->
        <div v-if="message.attachment" class="message-attachment-wrapper">
          <img v-if="message.attachment.type === 'image'"
            :src="message.attachment.url"
            :alt="message.attachment.filename"
            class="message-attachment-image"
            @click="openAttachment(message.attachment.url)" />
          <a v-else
            :href="message.attachment.url"
            target="_blank"
            class="message-attachment-doc">
            &#128196; {{ message.attachment.filename }}
          </a>
        </div>

        <!-- Streaming message: show raw text with typing cursor -->
        <div v-if="message.streaming" class="chat-message streaming-message">
          <div class="markdown-body streaming-text">
            <span>{{ message.content }}</span><span v-if="!message.statusMessage" class="streaming-cursor">▊</span>
          </div>
          <!-- Document generation progress indicator -->
          <div v-if="message.statusMessage" class="progress-indicator document-generation">
            <div class="progress-dots">
              <span class="progress-dot"></span>
              <span class="progress-dot"></span>
              <span class="progress-dot"></span>
            </div>
            <span class="progress-label">{{ message.statusMessage }}</span>
          </div>
        </div>

        <!-- Completed message with HTML content -->
        <div v-else-if="message.html_content" class="chat-message">
          <div
            class="markdown-body"
            v-html="message.html_content"
          ></div>
        </div>

        <!-- Plain text fallback -->
        <div v-else class="message-text">
          {{ message.content }}
        </div>

        <div v-if="message.role === 'assistant' && message.resource_id" class="feedback-buttons">
          <!-- TTS Play/Stop button -->
          <button
            v-if="ttsSupported"
            class="feedback-btn tts-btn"
            :class="{ active: currentlyPlayingIndex === index }"
            @click="toggleTTS(message, index)"
            :title="currentlyPlayingIndex === index ? 'Stop speaking' : 'Read aloud'"
          >
            <!-- Speaker icon (not playing) -->
            <svg v-if="currentlyPlayingIndex !== index" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
            </svg>
            <!-- Stop icon (currently playing) -->
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="6" y="6" width="12" height="12" rx="2"/>
            </svg>
          </button>
          <button
            class="feedback-btn"
            :class="{ active: message.feedback_vote === 1 }"
            @click="$emit('submit-feedback', message.resource_id, 1)"
            title="Thumbs up"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
            </svg>
          </button>
          <button
            class="feedback-btn"
            :class="{ active: message.feedback_vote === -1 }"
            @click="$emit('submit-feedback', message.resource_id, -1)"
            title="Thumbs down"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/>
              <path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Thinking indicator for non-streaming requests (file uploads, image generation, etc.) -->
    <div v-if="isThinking" class="message assistant">
      <div class="ai-icon" :style="aiIconStyle"></div>
      <div class="chat-wrapper">
        <div class="progress-indicator" :class="{ 'image-generation': isImageModel }">
          <div class="progress-dots">
            <span class="progress-dot"></span>
            <span class="progress-dot"></span>
            <span class="progress-dot"></span>
          </div>
          <span class="progress-label">{{ thinkingLabel }}</span>
        </div>
      </div>
    </div>

    <!-- TTS error toast -->
    <transition name="tts-toast">
      <div v-if="ttsError" class="tts-error-toast" @click="ttsError = null">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span>{{ ttsError }}</span>
      </div>
    </transition>
  </div>
</template>
<script>
import hljs from 'highlight.js'
import { onMounted, watch, nextTick } from "vue"
import "@/assets/styles/dracula.css"
 
export default {
  name: "ChatConversation",

  emits: ['submit-feedback'],

  props: {
    messages: {
      type: Array,
      required: true
    },
    companyInformation: {
      type: Object,
      default: null
    },
    isThinking: {
      type: Boolean,
      default: false
    },
    selectedChatbot: {
      type: Object,
      default: null
    },
    autoTtsOutput: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ttsSupported: false,
      currentlyPlayingIndex: null,
      ttsError: null,
      ttsErrorTimeout: null,
      lastAutoReadIndex: -1,
    }
  },
  computed: {
    aiIconStyle() {
      const faviconUrl = this.companyInformation?.favicon || require('@/assets/logo.svg');
      return {
        backgroundImage: `url(${faviconUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundSize: 'contain'
      };
    },
    isImageModel() {
      if (!this.selectedChatbot) return false;
      return (
        (this.selectedChatbot.extra_config && this.selectedChatbot.extra_config.image_generation) ||
        ['dall-e', 'dalle', 'gpt-image', 'image'].some(kw =>
          (this.selectedChatbot.model_name || '').toLowerCase().includes(kw)
        )
      );
    },
    thinkingLabel() {
      if (this.isImageModel) return 'Generating image...';
      return 'Thinking...';
    }
  },
  setup(props) {
    const injectCopyButtons = () => {
      const codeBlocks = document.querySelectorAll('div.codehilite pre code')

      codeBlocks.forEach((block) => {
        const wrapper = block.closest('.codehilite')
        if (!wrapper || wrapper.querySelector('.copy-btn')) return

        const button = document.createElement('button')
        button.className = 'copy-btn'
        button.innerText = 'Copy'

        button.onclick = () => {
          navigator.clipboard.writeText(block.innerText.trim())
          button.innerText = 'Copied!'
          setTimeout(() => {
            button.innerText = 'Copy'
          }, 1500)
        }

        wrapper.appendChild(button)
      })
    }

    const injectDownloadButtons = () => {
      // Find all AI-generated images and downloadable file links in chat
      const images = document.querySelectorAll('.chat-message .ai-generated-image, .chat-message img[src*="/media/"]')
      images.forEach((img) => {
        // Skip if already wrapped
        if (img.parentElement && img.parentElement.classList.contains('download-wrapper')) return

        // Create wrapper
        const wrapper = document.createElement('div')
        wrapper.className = 'download-wrapper'

        // Insert wrapper before img, then move img inside
        img.parentNode.insertBefore(wrapper, img)
        wrapper.appendChild(img)

        // Create download button
        const btn = document.createElement('a')
        btn.className = 'inline-download-btn'
        btn.href = img.src
        btn.download = img.alt || 'download'
        btn.title = 'Download'
        btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>'
        wrapper.appendChild(btn)
      })

      // Also handle downloadable file links (for Excel, PDF, CSV artifacts)
      const fileLinks = document.querySelectorAll('.chat-message a[href*="/media/"][href$=".xlsx"], .chat-message a[href*="/media/"][href$=".pdf"], .chat-message a[href*="/media/"][href$=".csv"]')
      fileLinks.forEach((link) => {
        // Skip if already has a download button sibling
        if (link.parentElement && link.parentElement.classList.contains('download-wrapper')) return
        if (link.classList.contains('inline-download-btn')) return

        // Create wrapper
        const wrapper = document.createElement('span')
        wrapper.className = 'download-wrapper download-wrapper-file'

        // Insert wrapper before link, then move link inside
        link.parentNode.insertBefore(wrapper, link)
        wrapper.appendChild(link)

        // Create download button
        const btn = document.createElement('a')
        btn.className = 'inline-download-btn inline-download-btn-file'
        btn.href = link.href
        btn.download = link.textContent || 'download'
        btn.title = 'Download'
        btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>'
        wrapper.appendChild(btn)
      })
    }

    onMounted(async () => {
      await nextTick()
      injectCopyButtons()
      injectDownloadButtons()
    })

     watch(
      () => props.messages,
      async () => {
        await nextTick();
        setTimeout(() => {
          injectCopyButtons();
          injectDownloadButtons();
      }, 50);
      },
      { deep: true, immediate: true }
    )

    return {}
  },

  methods: {
    scrollToBottom() {
      const el = this.$refs.chatConversation;
      if (!el) return;

      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    },
    extractLanguage(text) {
      const match = text.match(/```([a-zA-Z]*)/)
      return match && match[1] ? match[1] : 'plaintext'
    },

    highlightCode(text) {
      const code = text.replace(/```[a-zA-Z]*\n|```/g, '')
      return hljs.highlightAuto(code).value
    },

    copyCode(text) {
      const code = text.replace(/```[a-zA-Z]*\n|```/g, '')
      navigator.clipboard.writeText(code).then(() => {
        alert('Code copied to clipboard')
      })
    },
    openAttachment(url) {
      window.open(url, '_blank');
    },

    /**
     * Extract plain text from a message object (strip HTML tags).
     */
    getMessageText(message) {
      if (message.html_content) {
        // Strip HTML tags and decode entities
        const div = document.createElement('div');
        div.innerHTML = message.html_content;
        return div.textContent || div.innerText || '';
      }
      return message.content || '';
    },

    /**
     * Show a temporary TTS error message that auto-dismisses.
     */
    showTTSError(message) {
      this.ttsError = message;
      // Clear any existing timeout
      if (this.ttsErrorTimeout) {
        clearTimeout(this.ttsErrorTimeout);
      }
      // Auto-dismiss after 4 seconds
      this.ttsErrorTimeout = setTimeout(() => {
        this.ttsError = null;
        this.ttsErrorTimeout = null;
      }, 4000);
    },

    /**
     * Toggle TTS playback for a message.
     */
    toggleTTS(message, index) {
      // Clear any previous error
      this.ttsError = null;

      if (this.currentlyPlayingIndex === index) {
        // Stop current playback
        this.stopTTS();
        return;
      }

      // Stop any existing playback first
      this.stopTTS();

      const text = this.getMessageText(message);
      if (!text.trim()) {
        this.showTTSError('No text content to read aloud.');
        return;
      }

      // Try Azure TTS first, fall back to browser TTS
      this.playTTSAzure(text, index);
    },

    /**
     * Play TTS via Azure OpenAI endpoint (natural-sounding voice).
     * Falls back to browser TTS if server returns a browser fallback response.
     */
    async playTTSAzure(text, index) {
      this.currentlyPlayingIndex = index;

      try {
        const response = await fetch('/api/voice/synthesize/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          credentials: 'include',
          body: JSON.stringify({ text }),
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const contentType = response.headers.get('Content-Type') || '';

        if (contentType.includes('audio/')) {
          // Server returned audio data (Azure TTS) - play it
          const audioBlob = await response.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);

          // Store reference for stop functionality
          this._currentAudio = audio;

          audio.onended = () => {
            this.currentlyPlayingIndex = null;
            this._currentAudio = null;
            URL.revokeObjectURL(audioUrl);
          };

          audio.onerror = () => {
            this.currentlyPlayingIndex = null;
            this._currentAudio = null;
            URL.revokeObjectURL(audioUrl);
            // Fall back to browser TTS
            this.playTTSBrowser(text, index);
          };

          await audio.play();
        } else {
          // Server returned JSON (browser fallback)
          this.playTTSBrowser(text, index);
        }
      } catch (err) {
        console.warn('Azure TTS request failed, falling back to browser TTS:', err);
        // Fall back to browser TTS
        this.playTTSBrowser(text, index);
      }
    },

    /**
     * Get CSRF token from cookie.
     */
    getCsrfToken() {
      const value = `; ${document.cookie}`;
      const parts = value.split('; csrftoken=');
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    },

    /**
     * Play TTS using browser's Web Speech Synthesis API (fallback).
     */
    playTTSBrowser(text, index) {
      if (!window.speechSynthesis) {
        this.currentlyPlayingIndex = null;
        this.showTTSError('Text-to-speech is not supported in this browser.');
        return;
      }

      const synth = window.speechSynthesis;

      try {
        // Cancel any ongoing speech
        synth.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;

        // Try to use a natural-sounding voice
        const voices = synth.getVoices();
        const preferred = voices.find(v =>
          v.name.includes('Google') && v.lang.startsWith('en')
        ) || voices.find(v =>
          v.lang.startsWith('en') && v.localService
        ) || voices.find(v =>
          v.lang.startsWith('en')
        );
        if (preferred) {
          utterance.voice = preferred;
        }

        this.currentlyPlayingIndex = index;

        utterance.onend = () => {
          this.currentlyPlayingIndex = null;
        };

        utterance.onerror = (event) => {
          this.currentlyPlayingIndex = null;
          // Don't show error for 'interrupted' (user clicked stop) or 'canceled'
          if (event.error === 'interrupted' || event.error === 'canceled') {
            return;
          }
          const errorMessages = {
            'not-allowed': 'Speech synthesis was blocked by the browser. Please check your browser settings.',
            'network': 'Network error during speech synthesis. Please check your connection.',
            'synthesis-unavailable': 'Speech synthesis is temporarily unavailable. Please try again.',
            'synthesis-failed': 'Speech synthesis failed. Please try again.',
            'audio-busy': 'Audio output is busy. Please try again in a moment.',
            'language-unavailable': 'The selected language is not available for speech synthesis.',
          };
          const msg = errorMessages[event.error] || 'Unable to read aloud. Please try again.';
          this.showTTSError(msg);
        };

        synth.speak(utterance);
      } catch (err) {
        this.currentlyPlayingIndex = null;
        this.showTTSError('An unexpected error occurred with text-to-speech. Please try again.');
        console.error('TTS playback error:', err);
      }
    },

    /**
     * Check if auto-TTS should read the latest message.
     */
    checkAutoTts(msgCount) {
      if (!this.autoTtsOutput || !this.ttsSupported) return;
      if (msgCount <= 0) return;

      const lastIndex = msgCount - 1;
      const lastMsg = this.messages[lastIndex];

      // Only auto-read completed assistant messages (not streaming, not user messages)
      if (!lastMsg || lastMsg.role !== 'assistant' || lastMsg.streaming) return;
      // Don't re-read the same message
      if (lastIndex <= this.lastAutoReadIndex) return;

      // Must have content to read
      const text = this.getMessageText(lastMsg);
      if (!text || !text.trim()) return;

      // Mark as auto-read and play
      this.lastAutoReadIndex = lastIndex;
      this.$nextTick(() => {
        this.toggleTTS(lastMsg, lastIndex);
      });
    },

    /**
     * Stop any ongoing TTS playback (Azure audio or browser synthesis).
     */
    stopTTS() {
      // Stop Azure OpenAI audio if playing
      if (this._currentAudio) {
        this._currentAudio.pause();
        this._currentAudio.currentTime = 0;
        this._currentAudio = null;
      }
      // Stop browser speech synthesis
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      this.currentlyPlayingIndex = null;
    },
  },

 
  watch: {
    isThinking(val) {
      if (val) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    'messages.length'(newLen) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      });
      // Auto-TTS: when a new assistant message appears and is complete, auto-read it
      this.checkAutoTts(newLen);
    },
    // Auto-scroll during streaming as content updates
    messages: {
      handler(msgs) {
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg && lastMsg.streaming) {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
        // Check if a streaming message just completed (streaming flag turned off)
        if (lastMsg && !lastMsg.streaming && lastMsg.role === 'assistant' && lastMsg.html_content) {
          this.checkAutoTts(msgs.length);
        }
      },
      deep: true,
    }
  },

  mounted() {
    this.scrollToBottom();
    console.log('Messages received in ChatConversation.vue:', this.messages);

    // Check TTS support (desktop only - hide on mobile/touch-only devices)
    const isDesktop = window.innerWidth >= 768 && (window.matchMedia ? window.matchMedia('(pointer: fine)').matches : true);
    this.ttsSupported = ('speechSynthesis' in window) && isDesktop;

    // Preload voices (some browsers load them asynchronously)
    if (this.ttsSupported && window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  },

  beforeUnmount() {
    // Stop any ongoing TTS when component unmounts
    this.stopTTS();
    // Clear error timeout
    if (this.ttsErrorTimeout) {
      clearTimeout(this.ttsErrorTimeout);
    }
  }
}
</script>
 
<style>
.codehilite {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  font-family: 'Fira Code', Menlo, Consolas, monospace;
  font-size: 0.95rem;
  overflow-x: auto;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* Remove inner background from pre if needed */

.codehilite pre code {
  font-size: 0.95rem;
  padding: 0;
  line-height: 1.4;
  background: none;
  border: none;
}


/* Style the copy button */
.copy-btn {
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  z-index: 1;
}

.copy-btn:hover {
  opacity: 1;
  color: #000;
}

/* Feedback buttons */
.feedback-buttons {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.chat-wrapper:hover .feedback-buttons {
  opacity: 1;
}

/* Keep visible when a vote is active */
.feedback-buttons:has(.feedback-btn.active) {
  opacity: 1;
}

.feedback-btn {
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
  color: #888;
  display: flex;
  align-items: center;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.feedback-btn:hover {
  color: #333;
  border-color: #ccc;
}

.feedback-btn.active {
  color: var(--accent-color, #2563eb);
  border-color: var(--accent-color, #2563eb);
}

/* TTS play button - pulse animation when playing */
.tts-btn.active {
  color: var(--accent-color, #2563eb);
  border-color: var(--accent-color, #2563eb);
  animation: tts-pulse 1.5s ease-in-out infinite;
}

@keyframes tts-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.chat-message {
  margin-bottom: 20px;
}

.markdown-body pre,
.codehilite pre {
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: hidden;
}

pre code.language-plaintext{
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: hidden;
}
.chat-conversation {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  overflow-y: auto;
  padding: 1rem 2rem;
  padding-bottom: 160px;
  font-family: Arial, sans-serif;
  flex: 1;
  min-height: 0;
}

.chat-conversation::-webkit-scrollbar {
  display: none;
}

.message {
  display: flex;
  align-items: top;
  margin-bottom: 20px;
}

.message.user {
  justify-content: flex-start !important;
  align-items: flex-start !important;
}

.message.ai {
  justify-content: flex-start;
}

.ai-icon {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  margin-right: 10px;
  margin-left: 100px;
}

.message-text{
  
  word-break: break-word;
  overflow-wrap:anywhere;
  text-overflow: unset;
  font-size: 14px;

  max-width: 90%;
  border-radius: 10px;
  padding: 8px 12px;
  margin-right: 10px;
}

.message-text {
  
   max-width: 90%;
  margin-right: 10px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: anywhere; 
  line-height: 1.4rem;
  white-space: normal;
}

.message.assistant .message-text {
  background-color: #f4f4f4; 
  margin-left: 10px;
  border-radius: 10px;
  padding: 10px 15px;
  width: auto;
  max-width: 90%;

}

 .message.user .message-text,
.message.user .chat-message {
  background-color: #f4f4f4;
  margin-right: 10px;
  width: auto;
  max-width: 90%;
  padding: 13px;
  border-radius: 15px;
  font-size: 14px;
  line-height: 1.4rem;
}

/* AI messages without background color */
.message.ai .message-text {
  margin-left: 10px; /* Adjust to shift more to the right */
  background-color: transparent;
  color: inherit; /* Inherit the text color based on mode */
}

.message-code {
  background-color: #2d2d2d;
  color: #f8f8f2;
  font-family: monospace;
  padding: 15px;
  border-radius: 10px;
  position: relative;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.language-label {
  color: #f8f8f2;
  font-size: 0.9rem;
  font-weight: bold;
}


pre {
  white-space: pre-wrap;       /* Wraps text inside the code block */
  word-wrap: break-word;       /* Breaks long words onto the next line */
  overflow-wrap: break-word;   /* Ensures it wraps even in flexible layouts */
}
.example-container {
  display: flex;
  flex-direction: column;
  width: 100%; /* Take full width */
  max-width: 600px; /* Limit maximum width */
  margin: 0 auto; /* Center the container */
}

.example-item {
  width: 280px;
  height: 48px;
  background: #F7F9FB;
  border-radius: 8px;
  padding: 4px 8px;
  color: #1C1C1C;
  font-size: 14px;
  font-family: Inter, sans-serif;
  line-height: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.example-item:hover {
  background-color: #f0f0f0; /* Change background on hover */
}

.example-input {
  display: inline-block; /* Similar to example items */
  max-width: 100%; /* Prevent overflow from the container */
  white-space: nowrap; /* Stop input wrapping */
  overflow: hidden; /* Hide overflowing text */
  text-overflow: ellipsis; /* Add "..." for truncation on overflow */
  padding: 10px; /* Padding for user convenience */
  font-size: 14px; /* Slightly smaller font for inputs */
  margin-top: 10px; /* Separate it visually from example items */
  border: 1px solid #ddd;
  border-radius: 5px;
  box-sizing: border-box; /* Include padding in the width calculation */
}
@media (max-width: 768px) {
  .example-item,
  .example-input {
    font-size: 12px; /* Slightly smaller font for responsiveness */
    max-width: 100%; /* Ensure items fit the screen width */
    white-space: nowrap;
    margin-right: 0;
    margin-left: 0;  
  }


@media (max-width: 480px) {
  .example-item,
  .example-input {
    font-size: 11px; /* Further reduce font size on very small screens */
    max-width: 100%; /* Ensure space for both items and inputs */
    text-overflow: ellipsis; /* Retain truncation behavior */
  }
}

  /*.chat-conversation {
    max-height: 80vh; /* Limit the chat height
    overflow-y: auto;  Enable scrolling for large chat histories
  } */
}

.message-attachment-wrapper {
  margin-bottom: 8px;
}

.message-attachment-image {
  max-width: 300px;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: contain;
  border: 1px solid #e0e0e0;
}

.message-attachment-image:hover {
  opacity: 0.9;
}

.message-attachment-doc {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f0f2f5;
  border-radius: 8px;
  color: #1a73e8;
  text-decoration: none;
  font-size: 13px;
  border: 1px solid #e0e0e0;
}

.message-attachment-doc:hover {
  background: #e4e7eb;
}

/* AI-generated images and file outputs rendered via v-html */
.chat-message img,
.ai-generated-image {
  max-width: 100%;
  max-height: 500px;
  border-radius: 8px;
  margin: 8px 0;
  display: block;
}

/* Download button wrapper for generated images */
.download-wrapper {
  position: relative;
  display: inline-block;
  margin: 8px 0;
}

.download-wrapper img {
  margin: 0 !important;
}

.inline-download-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease;
  text-decoration: none;
  z-index: 2;
  backdrop-filter: blur(4px);
}

.download-wrapper:hover .inline-download-btn {
  opacity: 1;
}

.inline-download-btn:hover {
  background: rgba(0, 0, 0, 0.75);
}

.inline-download-btn svg {
  stroke: #fff;
}

/* Download button for file links (Excel, PDF, CSV) */
.download-wrapper-file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.inline-download-btn-file {
  position: static;
  width: 28px;
  height: 28px;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.08);
  color: #555;
  border-radius: 6px;
}

.inline-download-btn-file:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.15);
}

.inline-download-btn-file svg {
  stroke: #555;
}

/* File download link for generated CSV/Excel/PDF files */
.file-download-link {
  margin: 8px 0;
}

.artifact-inline-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #f0f4f8;
  color: #2563eb;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.15s, color 0.15s;
  border: 1px solid #d0d8e4;
}

.artifact-inline-download:hover {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #93c5fd;
}

/* Streaming message styles */
.streaming-message .streaming-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.streaming-cursor {
  display: inline;
  animation: cursor-blink 0.8s step-end infinite;
  color: var(--accent-color, #2563eb);
  font-weight: bold;
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Progress indicator for long-running tasks */
.progress-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f4f4f4;
  margin-bottom: 4px;
}

.progress-dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.progress-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #888;
  animation: progress-bounce 1.4s ease-in-out infinite both;
}

.progress-dot:nth-child(2) {
  animation-delay: 0.16s;
}

.progress-dot:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes progress-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.progress-label {
  font-size: 13px;
  color: #888;
  font-style: italic;
}

/* Image generation specific progress indicator */
.progress-indicator.image-generation .progress-dot {
  background: #4a90d9;
  animation: progress-bounce 1.4s ease-in-out infinite both;
}

.progress-indicator.image-generation .progress-label {
  color: #4a90d9;
}

/* Document generation specific progress indicator */
.progress-indicator.document-generation {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.progress-indicator.document-generation .progress-dot {
  background: #2eaa56;
  animation: progress-bounce 1.4s ease-in-out infinite both;
}

.progress-indicator.document-generation .progress-label {
  color: #2eaa56;
}

/* TTS error toast */
.tts-error-toast {
  position: sticky;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-width: 90%;
  width: fit-content;
  margin: 0 auto;
}

.tts-error-toast svg {
  flex-shrink: 0;
}

/* TTS toast transition */
.tts-toast-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.tts-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tts-toast-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.tts-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

/* Inline error message for failed AI responses (image generation, etc.) */
.inline-error-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
  line-height: 1.5;
}

.inline-error-message svg {
  color: #dc2626;
}

.inline-error-message p {
  margin: 0;
}

/* Hide TTS play buttons on mobile viewports */
@media (max-width: 767px) {
  .tts-btn {
    display: none !important;
  }
}

</style>